import React, { useMemo, useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { FaDownload, FaTrash } from "react-icons/fa";
import ConfirmModal from '../utils/ConfirmModal';
import ImageUploader from '../utils/ImageUploader';
import svgList from './svglist.json';

const NonLeafNode = ({ detail, setDetail, saveContent, handleImageDelete, handleImageChange }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [switchState, setSwitchState] = useState(detail.isLeaf);
  const [iconQuery, setIconQuery] = useState('');
  const { token, userInfo } = useContext(AppContext);
  const prefix = userInfo?.user?.S3Id ? `full/${userInfo.user.S3Id}/` : undefined;
  const thumbPrefix = userInfo?.user?.S3Id ? `thumb/${userInfo.user.S3Id}/` : undefined;
  const baseS3Url = svgList?.base_url || 'https://pladdypusv2.s3.ap-southeast-2.amazonaws.com/public/spot_icons/';

  const icons = useMemo(() => {
    if (!Array.isArray(svgList?.icons)) return [];

    const term = iconQuery.trim().toLowerCase();
    const mapped = svgList.icons
      .filter((icon) => icon?.value)
      .map((icon) => ({
        name: icon.name || icon.value.replace('.svg', ''),
        value: icon.value,
        url: `${baseS3Url}${icon.value}`,
      }));

    if (!term) return mapped;

    return mapped.filter((icon) =>
      icon.name.toLowerCase().includes(term) ||
      icon.value.toLowerCase().includes(term)
    );
  }, [baseS3Url, iconQuery]);
  const fieldLabelClass = "block text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]";
  const textInputClass = "mt-1 block w-full rounded-[18px] border border-[#dbe3f5] bg-white px-4 py-3 text-[14px] text-[#24324d] shadow-none outline-none transition-all duration-200 placeholder:text-[#99a4bc] hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:bg-white focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]";
  const sectionTitleClass = "block text-sm font-semibold text-[#334155]";

  const handleSwitch = () => {
    setIsModalOpen(true);
  };

  const confirmSwitch = () => {
    setIsModalOpen(false);
    setSwitchState(!switchState);
    setDetail((prevDetail) => ({
      ...prevDetail,
      isLeaf: true, description: '', bannerImage: '',
      iconImage: '', carouselImages: [], mainText: ''
    }));
  };

  const handleIconSelect = (icon) => {
    setDetail((prevDetail) => ({
      ...prevDetail,
      iconImage: icon.url,
      brandImagePrivate: icon.value,
    }));
  };

  const handleIconClear = () => {
    setDetail((prevDetail) => ({
      ...prevDetail,
      iconImage: '',
      brandImagePrivate: '',
    }));
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <button
          onClick={handleSwitch}
          className="inline-flex items-center rounded-xl border border-[#ffd9e0] bg-[#fff5f7] px-4 py-2 text-sm font-medium text-[#ff4d6d] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#ffc5cf] hover:bg-[#fff0f3] hover:shadow-[0_10px_20px_rgba(255,77,109,0.12)]"
        >
          Switch to Directory Entry?
        </button>
      </div>
      <ConfirmModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmSwitch}
        header="Do you want to proceed?"
        content="Switching will clear all data and change to Content mode."
      />
      <div className="flex flex-col md:flex-row md:space-x-4">
        <div className='w-full md:w-1/2 p-1'>
          <label className='block mt-4'>
            <span className={fieldLabelClass}>
              Name
              <span className='text-xs'> &nbsp;{detail.name ? detail.name.length : 0}/30</span>
            </span>
            <input
              type="text"
              name="name"
              value={detail.name || ''}
              onChange={(e) => setDetail({ ...detail, name: e.target.value })}
              maxLength={30}
              className={textInputClass}
            />
          </label>
          <label className='block mt-4'>
            <span className={fieldLabelClass}>
              How to show inner content
            </span>
            <span className="flex items-center">
              <span
                onClick={() => { setDetail({ ...detail, layoutStyle: detail.layoutStyle === "sidebar" ? "overlay" : "sidebar" }); }}
                className={`relative w-12 h-6 flex items-center rounded-full cursor-pointer ${detail.layoutStyle === "sidebar" ? 'bg-blue-500' : 'bg-gray-400'
                  }`}
              >
                <span
                  className={`absolute w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-300 ${detail.layoutStyle === "sidebar" ? 'translate-x-6' : 'translate-x-1'
                    }`}
                ></span>
              </span>
              <p className="ml-4 text-sm font-medium text-[#334155]">{detail.layoutStyle === "sidebar" ? 'Show icon' : 'Show image'}</p>
            </span>
          </label>
          <div className='block mt-2'>
            <span className={sectionTitleClass}>Banner Image</span>
            <div className="flex items-center">
              <div className="mt-1">
                <ImageUploader
                  token={token}
                  prefix={prefix}
                  thumbPrefix={thumbPrefix}
                  width={960}
                  height={540}
                  objectFit="cover"
                  buttonText="Edit Banner Image"
                  value={detail.bannerImage}
                  onChange={(url) => {
                    setDetail((prevDetail) => ({
                      ...prevDetail,
                      bannerImage: url,
                    }));
                  }}
                />
              </div>

            </div>
          </div>
          {detail.bannerImage && (
            <div className="mt-3 w-full overflow-hidden rounded-[20px] border border-[#e6ebf8] bg-[#f8faff] p-3">
              <img
                src={detail.bannerImage}
                alt="sideImage"
                className="block w-full rounded-[18px] border border-[#dbe3f5] bg-white shadow-[0_10px_20px_rgba(15,23,42,0.06)]"
              />
              <a href={detail.bannerImage} download="sideImage.jpg">
                <FaDownload
                  className="absolute left-5 top-5 z-10 h-6 w-6 cursor-pointer text-[#2557d6] transition-transform duration-200 hover:scale-110"
                />
              </a>
              <FaTrash
                className="absolute right-5 top-5 z-10 h-6 w-6 cursor-pointer text-red-500 transition-transform duration-200 hover:scale-110"
                onClick={() => handleImageDelete("bannerImage")}
              />
            </div>
          )}

        </div>
        <div className='w-full p-1'>
          <div className="block mt-2">
            <div className='flex items-center gap-2'>
              <span className={sectionTitleClass}>Display Icon</span>
              {detail.iconImage && (
                <img
                  src={detail.iconImage}
                  alt="selected icon"
                  className="h-12 w-12 object-contain"
                />
              )}
              {detail.iconImage && (
                <FaTrash
                  className="h-4 w-4 text-red-500 cursor-pointer ml-auto"
                  onClick={handleIconClear}
                />
              )}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input
                type="text"
                value={iconQuery}
                onChange={(event) => setIconQuery(event.target.value)}
                placeholder="Search icons..."
                className="block w-full rounded-[18px] border border-[#dbe3f5] bg-white px-4 py-3 text-sm text-[#24324d] shadow-none outline-none transition-all duration-200 placeholder:text-[#99a4bc] hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]"
              />
              <button
                type="button"
                className="rounded-xl border border-[#ffd9e0] bg-[#fff5f7] px-4 py-2 text-sm font-medium text-[#ff4d6d] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#ffc5cf] hover:bg-[#fff0f3] hover:shadow-[0_10px_20px_rgba(255,77,109,0.12)] disabled:opacity-50"
                onClick={handleIconClear}
                disabled={!detail.iconImage}
              >
                Clear
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3 max-h-80 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-3 sm:grid-cols-6 xl:grid-cols-8">
              {icons.map((icon) => {
                const isSelected = detail.iconImage === icon.url;
                return (
                  <button
                    key={icon.value}
                    type="button"
                    onClick={() => handleIconSelect(icon)}
                    className={`cursor-pointer rounded-md p-2 text-left ${isSelected ? 'border-2 border-blue-500 bg-blue-50' : 'border border-gray-300'
                      }`}
                    title={icon.name}
                  >
                    <div className="flex h-12 items-center justify-center">
                      <img
                        src={icon.url}
                        alt={icon.name}
                        className="max-h-10 max-w-10 object-contain"
                      />
                    </div>
                    <div className="mt-1 truncate text-[10px] text-slate-600">{icon.name}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default NonLeafNode;
